<!-- src/components/Financeiro/Receitas/FormularioReceitaGeral.vue -->
<template>
  <div class="form-container card shadow-sm p-3">
    <div v-if="contasMensagem" class="alert alert-warning" role="alert">{{ contasMensagem }}</div>
    <!-- Cabeçalho -->
    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ receita.id ? `Editar Receita Geral nº ${receita.id}` : 'Nova Receita Geral' }}
      </h4>
      <button class="btn btn-secondary" @click="cancelar">Voltar</button>
    </div>

    <div class="spacer"></div>

    <!-- Seção 1: Identificação da Receita -->
    <div class="form-section">
      <h5 class="section-title">Identificação da Receita</h5>
      <div class="row">
        <!-- Plano de Contas -->
        <div class="col-md-4">
          <div class="form-group">
            <label>Plano de Contas <span class="required">*</span></label>
            <select
              v-model.number="receita.subconta"
              class="form-control"
              required
            >
              <option value="">Selecione</option>
              <option
                v-for="sc in subcontas"
                :key="sc.id"
                :value="sc.id"
              >
                {{ sc.nome }}
              </option>
            </select>
          </div>
        </div>

        <!-- Descrição -->
        <div class="col-md-8">
          <div class="form-group">
            <label>Descrição <span class="required">*</span></label>
            <input
              type="text"
              v-model="receita.descricao"
              class="form-control"
              required
            />
          </div>
        </div>
      </div>
    </div>

    <div class="spacer"></div>

    <!-- Seção 2: Detalhes do Pagamento -->
    <div class="form-section">
      <h5 class="section-title">Detalhes do Pagamento</h5>
      <div class="row mb-3">
        <!-- Data do registro -->
        <div class="col-md-2">
          <div class="form-group">
            <label>Data do Registro</label>
            <input
              type="date"
              class="form-control disabled-input"
              :value="dataRegistro"
              disabled
            />
          </div>
        </div>
        <!-- Valor total -->
        <div class="col-md-2">
          <div class="form-group">
            <label>Valor <span class="required">*</span></label>
            <input
              type="text"
              :value="valorStr"
              inputmode="decimal"
              @input="onValorInput"
              class="form-control"
              required
            />
          </div>
        </div>
        <!-- Recorrência (parcelas) -->
        <div class="col-md-1">
          <div class="form-group">
            <label>Recorrência</label>
            <input
              type="number"
              v-model.number="parcelasCount"
              class="form-control"
              min="1"
              required
            />
          </div>
        </div>

        <!-- Período -->
        <div class="col-md-3" v-if="parcelasCount > 1">
          <div class="form-group">
            <label>Período</label>
            <select
              v-model="receita.periodicidade"
              class="form-control"
              required
            >
              <option value="mensal">Mensal (a cada 30 dias)</option>
              <option value="quinzenal">Quinzenal (a cada 15 dias)</option>
              <option value="semanal">Semanal (a cada 7 dias)</option>
              <option value="outro">Outro período</option>
            </select>
          </div>
        </div>
        <!-- Informe período em dias -->
        <div class="col-md-3" v-if="parcelasCount > 1 && receita.periodicidade === 'outro'">
          <label>Período (dias)</label>
          <input
            type="number"
            v-model.number="receita.periodicidade_dias"
            class="form-control"
            min="1"
            required
          />
        </div>

        <!-- Primeiro vencimento -->
        <div class="col-md-2" v-if="parcelasCount > 1">
          <div class="form-group">
            <label>1º Vencimento <span class="required">*</span></label>
            <input
              type="date"
              v-model="receita.primeiro_vencimento"
              class="form-control"
              required
            />
          </div>
        </div>
      </div>

      <!-- Tabela de recebimentos -->
      <div class="table-responsive" v-if="parcelasCount >= 1">
        <table class="table table-borderless">
          <thead>
            <tr>
              <th>#</th>
              <th>Forma de Pagamento <span class="required">*</span></th>
              <th>Autorização</th>
              <th>Vencimento <span class="required">*</span></th>
              <th>Valor</th>
              <th>Pagamento</th>
              <th>Recebimento</th>
              <th>Conta Financeira <span class="required">*</span></th>
              <th v-if="showChequeCols">Banco nº</th>
              <th v-if="showChequeCols">Agência</th>
              <th v-if="showChequeCols">Cheque nº</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in pagamentos" :key="i">
              <td>{{ i + 1 }}</td>

              <td>
                <select
                  v-model.number="p.forma_pagamento_id"
                  class="form-control"
                  required
                  @change="onPagamentoFormaChange(p)"
                >
                  <option value="">Selecione</option>
                  <option v-for="fp in formasPagamento" :key="fp.id" :value="fp.id">
                    {{ fp.nome }}
                  </option>
                </select>
              </td>

              <td>
                <input
                  v-if="isFormaComAutorizacao(p.forma_pagamento_id)"
                  v-model="p.numero_autorizacao"
                  type="text"
                  class="form-control"
                  placeholder="Autorização"
                  @input="onNumeroAutorizacaoPagamentoInput(p)"
                  :disabled="isAInformar(p.forma_pagamento_id)"
                />
              </td>

              <td>
                <input
                  type="date"
                  v-model="p.data_vencimento"
                  @change="onParcelaChange(p)"
                  class="form-control"
                />
              </td>

              <td>
                <input
                  type="text"
                  :value="formatCurrency(p.valor)"
                  class="form-control"
                  readonly
                />
              </td>

              <td>
                <input
                  type="date"
                  v-model="p.data_pagamento"
                  @change="onDataPagamentoChange(p)"
                  :disabled="isAInformar(p.forma_pagamento_id)"
                  class="form-control"
                />
              </td>

              <td>
                <input
                  type="date"
                  v-model="p.data_recebimento"
                  @change="onRecebimentoEdit(p)"
                  class="form-control"
                />
              </td>

              <td>
                <select
                  v-model.number="p.conta_financeira_id"
                  class="form-control"
                  :disabled="isAInformar(p.forma_pagamento_id) || contaOperacionalBloqueada(p)"
                  :required="!isAInformar(p.forma_pagamento_id) && !!(p.data_pagamento && p.data_pagamento.trim())"
                  @change="onContaFinanceiraPagamentoChange(p)"
                >
                  <option v-if="isAInformar(p.forma_pagamento_id)" :value="null">A informar</option>

                  <template v-else>
                    <option :value="null" disabled>Selecione</option>
                    <option v-for="c in contasFinanceiras" :key="c.id" :value="Number(c.id)">
                      {{ c.texto }}
                    </option>
                  </template>
                </select>
              </td>

              <td v-if="showChequeCols">
                <input
                  v-if="p.forma_pagamento_id === idCheque"
                  v-model="p.banco_numero"
                  type="text"
                  class="form-control"
                  placeholder="Banco nº"
                  required
                />
              </td>

              <td v-if="showChequeCols">
                <input
                  v-if="p.forma_pagamento_id === idCheque"
                  v-model="p.agencia"
                  type="text"
                  class="form-control"
                  placeholder="Agência"
                  required
                />
              </td>

              <td v-if="showChequeCols">
                <input
                  v-if="p.forma_pagamento_id === idCheque"
                  v-model="p.cheque_numero"
                  type="text"
                  class="form-control"
                  placeholder="Cheque nº"
                  required
                />
              </td>

              <td>
                <i
                  class="bi bi-trash-fill text-danger"
                  title="Remover parcela"
                  v-if="(!p.id || hasPermission('excluir_receitas_gerais')) && p.data_pagamento && new Date(p.data_pagamento) > new Date()"
                  @click="removerParcela(i)"
                  style="cursor: pointer;"
                ></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Ações -->
    <div class="form-actions d-flex justify-content-end">
      <button
        class="btn btn-primary me-2"
        :disabled="!formValid || isSubmitting"
        v-if="hasPermission(savePermission)"
        @click="salvar"
      >
        <i class="bi bi-save me-1"></i>
        {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
      </button>
      <button
        class="btn btn-secondary"
        :disabled="isSubmitting"
        @click="cancelar"
      >
        <i class="bi bi-x-circle me-1"></i>
        Cancelar
      </button>
    </div>
  </div>
</template>

<script setup>
import { paymentCollectionOperations } from "@/utils/operationalPaymentPayload";
import { loadOperationalAccounts, accountSelectionError, emptyAccountsMessage } from '@/services/operationalAccounts';
let originalPayments = [];
const contaOperacionalBloqueada = payment => {
  const original = originalPayments.find(row => Number(row.id) === Number(payment.id));
  return !!original && !!(original.data_pagamento || original.data_recebimento || original.pago ||
    (original.cheque_status && original.cheque_status !== 'NORMAL'));
};
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from '@/services/axios';
import { useReceitasPermissions } from '@/composables/useReceitasPermissions';
import globalUtils from '@/utils/globalUtils.js';

const router = useRouter();
const route = useRoute();
const { fetchPermissions, hasPermission, authorizeReceiptAction } = useReceitasPermissions();
const savePermission = computed(() => route.params.id ? 'alterar_receitas_gerais' : 'adicionar_receitas_gerais');

const hydrating = ref(true);

// Estado principal
const receita = reactive({
  id: null,
  subconta: null,
  descricao: '',
  criado_em: '',
  valor_total: 0,
  periodicidade: 'mensal',
  periodicidade_dias: null,
  primeiro_vencimento: '',
});

// Controles
const subcontas = ref([]);
const tiposPlano    = ref([]);
const tipoSelecionado = ref('Receita'); // padrão
const formasPagamento = ref([]);
const contasFinanceiras = ref([]);
const contasMensagem = ref('');
const pagamentos = ref([]);
const parcelasCount = ref(1);
const isSubmitting = ref(false);

const norm = s => (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim().toLowerCase();

const getFormaById = (id) => formasPagamento.value.find(f => Number(f.id) === Number(id));

const isCartaoCreditoAVista = (formaId) => {
  const n = norm(getFormaById(formaId)?.nome);
  return (
    n === 'cartao de credito' ||
    n === 'cartao de credito a vista' ||
    (n.includes('cartao') && n.includes('credito') && n.includes('vista'))
  );
};

const isCartaoCreditoParcelado = (formaId) => {
  const n = norm(getFormaById(formaId)?.nome);
  return n.includes('cartao') && n.includes('credito') && n.includes('parcelado');
};

const isCartaoDebito = (formaId) => {
  const n = norm(getFormaById(formaId)?.nome);
  return n.includes('cartao') && n.includes('debito');
};

const isCheque = (formaId) => {
  const n = norm(getFormaById(formaId)?.nome);
  return n === 'cheque';
};

const isFormaComAutorizacao = (formaId) => {
  return (
    isCartaoCreditoAVista(formaId) ||
    isCartaoCreditoParcelado(formaId) ||
    isCartaoDebito(formaId)
  );
};

const getPagamentosOrdenados = () => {
  return pagamentos.value
    .slice()
    .sort((a, b) => String(a?.data_vencimento || '').localeCompare(String(b?.data_vencimento || '')));
};

const getBlocoParceladoDoPagamento = (pagamento) => {
  const lista = getPagamentosOrdenados();
  const idx = lista.findIndex(p =>
    p === pagamento || (p?.id && pagamento?.id && Number(p.id) === Number(pagamento.id))
  );

  if (idx === -1) return [];
  if (!isCartaoCreditoParcelado(lista[idx]?.forma_pagamento_id)) return [lista[idx]];

  let ini = idx;
  let fim = idx;

  while (ini > 0 && isCartaoCreditoParcelado(lista[ini - 1]?.forma_pagamento_id)) ini--;
  while (fim < lista.length - 1 && isCartaoCreditoParcelado(lista[fim + 1]?.forma_pagamento_id)) fim++;

  return lista.slice(ini, fim + 1);
};

const getNumeroParcelaNoBlocoParcelado = (pagamento) => {
  const bloco = getBlocoParceladoDoPagamento(pagamento);
  const idx = bloco.findIndex(p =>
    p === pagamento || (p?.id && pagamento?.id && Number(p.id) === Number(pagamento.id))
  );
  return idx >= 0 ? idx + 1 : 1;
};

const isInicioBlocoParcelado = (pagamento) => {
  if (!isCartaoCreditoParcelado(pagamento?.forma_pagamento_id)) return false;

  const lista = getPagamentosOrdenados();
  const idx = lista.findIndex(p =>
    p === pagamento || (p?.id && pagamento?.id && Number(p.id) === Number(pagamento.id))
  );

  if (idx === -1) return false;
  if (idx === 0) return true;

  return !isCartaoCreditoParcelado(lista[idx - 1]?.forma_pagamento_id);
};

const propagarBlocoParceladoParaLinhasVazias = (pagamento) => {
  if (!isInicioBlocoParcelado(pagamento)) return;

  const lista = getPagamentosOrdenados();
  const idx = lista.findIndex(p =>
    p === pagamento || (p?.id && pagamento?.id && Number(p.id) === Number(pagamento.id))
  );

  if (idx === -1) return;

  const autorizacao = (pagamento.numero_autorizacao || '').trim();
  const conta = pagamento.conta_financeira_id ?? null;

  for (let i = idx + 1; i < lista.length; i++) {
    const atual = lista[i];
    const formaAtual = atual?.forma_pagamento_id;

    // para ao encontrar uma próxima linha já definida
    if (formaAtual !== null && formaAtual !== '' && formaAtual !== undefined) {
      break;
    }

    atual.forma_pagamento_id = pagamento.forma_pagamento_id;

    if (!(atual.numero_autorizacao || '').trim() && autorizacao) {
      atual.numero_autorizacao = autorizacao;
    }

    if (
      (atual.conta_financeira_id === null ||
        atual.conta_financeira_id === '' ||
        atual.conta_financeira_id === undefined) &&
      conta
    ) {
      atual.conta_financeira_id = conta;
    }

    if (!atual.data_pagamento) {
      atual.data_pagamento = atual.data_vencimento || '';
    }
  }
};

const getDataBaseBlocoParcelado = (pagamento) => {
  const bloco = getBlocoParceladoDoPagamento(pagamento);
  const primeira = bloco?.[0];

  if (!primeira) return null;

  return (
    primeira.data_pagamento ||
    primeira.data_vencimento ||
    pagamento.data_pagamento ||
    pagamento.data_vencimento ||
    null
  );
};

const replicarAutorizacaoNoBlocoParcelado = (pagamento) => {
  if (!isCartaoCreditoParcelado(pagamento?.forma_pagamento_id)) return;
  const bloco = getBlocoParceladoDoPagamento(pagamento);
  const valor = (pagamento.numero_autorizacao || '').trim();

  bloco.forEach(p => {
    p.numero_autorizacao = valor || null;
  });
};

const replicarContaNoBlocoParcelado = (pagamento) => {
  if (!isCartaoCreditoParcelado(pagamento?.forma_pagamento_id)) return;
  const bloco = getBlocoParceladoDoPagamento(pagamento);
  const conta = pagamento.conta_financeira_id ?? null;

  bloco.forEach(p => {
    p.conta_financeira_id = conta;
  });
};

const onNumeroAutorizacaoPagamentoInput = (pagamento) => {
  if (isCartaoCreditoParcelado(pagamento?.forma_pagamento_id)) {
    replicarAutorizacaoNoBlocoParcelado(pagamento);
  }
};

const onContaFinanceiraPagamentoChange = (pagamento) => {
  if (isCartaoCreditoParcelado(pagamento?.forma_pagamento_id)) {
    replicarContaNoBlocoParcelado(pagamento);
  }
};

const contaPadraoGlobalId = computed(() => {
  const c = (contasFinanceiras.value || []).find(x => x.conta_padrao === true || x.conta_padrao === 1);
  return c?.id ? Number(c.id) : null;
});

const aplicarContaPadraoSeVazio = (formaId, p) => {
  const forma = getFormaById(formaId);
  if (!forma || !p) return;

  const nome = norm(forma.nome);
  if (nome === 'especie' || nome === 'a informar' || nome === 'informar') return;

  if (p.conta_financeira_id != null && p.conta_financeira_id !== '') return;

  const porForma = forma.conta_financeira_padrao_id ? Number(forma.conta_financeira_padrao_id) : null;
  if (porForma) { p.conta_financeira_id = porForma; return; }

  if (contaPadraoGlobalId.value) p.conta_financeira_id = contaPadraoGlobalId.value;
};

const idBrinde = computed(() =>
  formasPagamento.value.find(fp => norm(fp.nome) === 'brinde')?.id ?? null
);

const hasBrinde = computed(() =>
  pagamentos.value.some(p => p.forma_pagamento_id === idBrinde.value)
);


// ID da forma “A informar”
const idAInformar = computed(() =>
  formasPagamento.value.find(fp => fp.nome.toLowerCase() === 'a informar')?.id || null
);

// helper para saber se é “A informar”
function isAInformar(formaId) {
  return formaId === idAInformar.value;
}

// Identifica forma 'Cheque'
const idCheque = computed(() =>
  formasPagamento.value.find(fp => fp.nome.toLowerCase() === 'cheque')?.id || null
);

const showChequeCols = computed(() =>
  pagamentos.value.some(p => p.forma_pagamento_id === idCheque.value)
);


// === Máscara pt-BR para o campo Valor (9.999,99) ===
const valorStr = ref(''); // exibição no input

const nfBR = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Converte string pt-BR -> Number JS (ponto decimal)
// function parsePtBR(str) {
//   if (!str) return 0;
//   const cleaned = String(str).replace(/\./g, '').replace(',', '.');
//   const n = parseFloat(cleaned);
//   return Number.isNaN(n) ? 0 : n;
// }

function onRecebimentoEdit(p) {
  p._recebimento_manual = true; // preserva valor editado
}

function recalcRecebimento(p) {
  const fp = p?.forma_pagamento_id;

  if (!p.data_pagamento || !fp || isAInformar(fp)) {
    p.data_recebimento = '';
    return;
  }

  if (isCartaoCreditoParcelado(fp)) {
    const numeroParcela = getNumeroParcelaNoBlocoParcelado(p);
    const dataBaseOperacao = getDataBaseBlocoParcelado(p);

    if (!dataBaseOperacao) {
      p.data_recebimento = '';
      return;
    }

    p.data_recebimento = globalUtils.calcularDataRecebimento(
      dataBaseOperacao,
      fp,
      formasPagamento.value,
      numeroParcela
    ) || '';
    return;
  }

  p.data_recebimento = globalUtils.calcularDataRecebimento(
    p.data_pagamento,
    fp,
    formasPagamento.value,
    1
  ) || '';
}

function recalcRecebimentoBlocoParcelado(pagamento) {
  const bloco = getBlocoParceladoDoPagamento(pagamento);
  if (!bloco.length) return;

  const primeira = bloco[0];

  if (!primeira.data_pagamento) {
    primeira.data_pagamento = primeira.data_vencimento || '';
  }

  const dataBaseOperacao = getDataBaseBlocoParcelado(primeira);

  bloco.forEach((p, idx) => {
    if (!p.data_pagamento) {
      p.data_pagamento = p.data_vencimento || '';
    }

    if (!dataBaseOperacao) {
      p.data_recebimento = '';
      return;
    }

    p.data_recebimento = globalUtils.calcularDataRecebimento(
      dataBaseOperacao,
      p.forma_pagamento_id,
      formasPagamento.value,
      idx + 1
    ) || '';
  });
}

function onDataPagamentoChange(p) {
  onParcelaChange(p);
  p._recebimento_manual = false;

  if (isCartaoCreditoParcelado(p.forma_pagamento_id)) {
    recalcRecebimentoBlocoParcelado(p);
  } else {
    recalcRecebimento(p);
  }
}

// Handler de digitação com máscara (mantém 2 casas)
function onValorInput(e) {
  // Mantém apenas dígitos
  const onlyDigits = e.target.value.replace(/[^\d]/g, '');
  // Se vazio, zera
  if (!onlyDigits.length) {
    valorStr.value = '';
    receita.valor_total = 0;
    return;
  }
  // Converte para centavos -> número
  const n = Number(onlyDigits) / 100;
  valorStr.value = nfBR.format(n); // exibe "9.999,99"
  receita.valor_total = n;         // mantém número (9xxx.yy) no modelo
}

function aplicarValorEmTodasParcelas() {
  const v = Number(receita.valor_total) || 0;
  pagamentos.value.forEach(p => { p.valor = v; });
}

// Data do registro (hoje ou criado_em)
const dataRegistro = computed(() =>
  receita.id ? receita.criado_em.split('T')[0] : (new Date()).toISOString().slice(0,10)
);

// Form validação
const formValid = computed(() => {
  if (!receita.subconta || !receita.descricao) return false;
  if (parcelasCount.value < 1) return false;

  // Permite valor 0 quando houver pelo menos uma parcela "Brinde"
  const valorOk = hasBrinde.value ? true : Number(receita.valor_total) > 0;
  if (!valorOk) return false;

  return pagamentos.value.every(p => {
    if (!p.forma_pagamento_id || !p.data_vencimento) return false;

    const fp = p.forma_pagamento_id;

    // BRINDE: não exige nada além de forma/vencimento
    if (idBrinde.value != null && fp === idBrinde.value) {
      // opcional: se quiser exigir coerência quando registrar como pago:
      const temData  = !!(p.data_pagamento && String(p.data_pagamento).trim());
      const temConta = (p.conta_financeira_id != null);
      if (temData !== temConta) return false; // se marcou como pago, exige os dois
      return true;
    }

    // A informar: não exige pagamento/conta/cheque
    if (isAInformar(fp)) return true;

    // Se informou pagamento → exige conta (inclui Espécie)
    if ((p.data_pagamento || '').trim() && !p.conta_financeira_id) return false;

    // Cheque quitado (pagamento informado) exige dados
    if (fp === idCheque.value && (p.data_pagamento || '').trim()) {
      if (!p.banco_numero || !p.agencia || !p.cheque_numero) return false;
    }

    // Cartão: exige autorização na linha (segurança extra)
    if (isFormaComAutorizacao(fp) && !(p.numero_autorizacao || '').trim()) return false;

    return true;
  });
});

// Formata moeda
function formatCurrency(v) {
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Gera array de pagamentos
function gerarPagamentos(count) {
  const total = Number(receita.valor_total) || 0;
  // cada linha recebe o valor total, sem dividir
  const base = total;
  const arr = [];
  for (let i = 0; i < count; i++) {
    // calcula vencimento
    let dias = 30;
    switch (receita.periodicidade) {
      case 'quinzenal': dias = 15; break;
      case 'semanal': dias = 7; break;
      case 'outro': dias = Number(receita.periodicidade_dias) || 1; break;
    }
    const dt0 = new Date(receita.primeiro_vencimento);
    dt0.setDate(dt0.getDate() + dias * i);

    if (i > 0) {
      // se cair em fim de semana/feriado, empurra para o próximo útil
      const isWeekend = [0, 6].includes(dt0.getDay());
      const ajustada = isWeekend ? globalUtils.nextBusinessDay(dt0) : dt0;
      // se quiser garantir também contra feriado (mesmo quando não é w/e), faça:
      // const ajustada = globalUtils.nextBusinessDay(dt0);
      dt0.setTime(ajustada.getTime());
    }

    const data_venc = dt0.toISOString().slice(0,10);
    arr.push({
      id: null,
      receitas_gerais_id: receita.id,
      forma_pagamento_id: null,
      data_vencimento: data_venc,
      valor: base,
      data_pagamento: '',
      conta_financeira_id: null,
      banco_numero: '',
      agencia: '',
      cheque_numero: '',
      observacoes: '',
      numero_autorizacao: null,
      data_recebimento: '',
    });
  }
  pagamentos.value = arr;
}


watch(
  [() => receita.periodicidade, () => receita.periodicidade_dias, () => receita.primeiro_vencimento],
  async () => {
    if (!receita.id && parcelasCount.value >= 1 && receita.primeiro_vencimento) {
      gerarPagamentos(parcelasCount.value); // troca o array inteiro
      await nextTick();
      aplicarValorEmTodasParcelas();        // repropaga o valor
    }
  }
);

watch(
  () => receita.valor_total,
  async () => {
    // Garante que qualquer troca do array pagamentos já aconteceu
    await nextTick();
    aplicarValorEmTodasParcelas();
  },
  { flush: 'post' } // executa depois dos efeitos sincronizados
);

watch(
  () => pagamentos.value.length,
  () => {
    aplicarValorEmTodasParcelas();
  }
);


// Quando o usuário muda vencimento ou forma, nada a recalcular além do próprio campo pagamento
function onParcelaChange(p) {
  const isBrinde = (idBrinde.value != null && p.forma_pagamento_id === idBrinde.value);

  // Se for Brinde → zera valor
  if (isBrinde) {
    p.valor = 0;
  }

  const nome = (formasPagamento.value.find(f => f.id === p.forma_pagamento_id)?.nome || '').toLowerCase();

  // 1) Pagamento
  if (nome === 'a informar') {
    p.data_pagamento = '';
  } 
  else if (!p.data_pagamento) {
    p.data_pagamento = p.data_vencimento;
  }

  // 2) Recebimento: não recalcula aqui; só limpa quando inválido
  if (!p.data_pagamento || !p.forma_pagamento_id || isAInformar(p.forma_pagamento_id)) {
    p.data_recebimento = '';
  }
}

function onPagamentoFormaChange(p) {
  const fp = p.forma_pagamento_id;

  if (!fp) {
    p.data_pagamento = '';
    p.data_recebimento = '';
    p.conta_financeira_id = null;
    p.numero_autorizacao = null;
    return;
  }

  const isBrinde = (idBrinde.value != null && fp === idBrinde.value);

  if (isBrinde) {
    p.valor = 0;
    p.numero_autorizacao = null;
    p.banco_numero = '';
    p.agencia = '';
    p.cheque_numero = '';
    return;
  }

  p.conta_financeira_id = null;
  p.numero_autorizacao = null;

  if (isAInformar(fp)) {
    p.data_pagamento = '';
    p.data_recebimento = '';
    return;
  }

  if (!isCheque(fp)) {
    p.banco_numero = '';
    p.agencia = '';
    p.cheque_numero = '';
  }

  if (!p.data_pagamento) {
    p.data_pagamento = p.data_vencimento || '';
  }

  aplicarContaPadraoSeVazio(fp, p);

  if (isCartaoCreditoParcelado(fp)) {
    propagarBlocoParceladoParaLinhasVazias(p);

    const bloco = getBlocoParceladoDoPagamento(p);

    const comAutorizacao = bloco.find(x => (x.numero_autorizacao || '').trim());
    if (comAutorizacao) {
      p.numero_autorizacao = (comAutorizacao.numero_autorizacao || '').trim() || null;
    }

    const comConta = bloco.find(x => x.conta_financeira_id != null);
    if (comConta) {
      p.conta_financeira_id = comConta.conta_financeira_id;
    }

    replicarAutorizacaoNoBlocoParcelado(p);
    replicarContaNoBlocoParcelado(p);
    recalcRecebimentoBlocoParcelado(p);
    return;
  }

  recalcRecebimento(p);
}

async function carregarListas(tipo = 'Receita') {
  const respSc = await axios.get('/financeiro/subcontas', {
    params: {
      tipo,                // <<<<<< agora usa o parâmetro (padrão 'Receita')
      ativo: true,         // use booleano em vez de string
      sortField: 'nome',
      sortOrder: 'asc',
      limit: 'all',
    },
  });

  // Lista vinda da API
  let lista = respSc.data?.data ?? respSc.data ?? [];

  // Filtro de segurança no front, caso o back não respeite o 'tipo'
  const norm = s => (s ?? '').toString().trim().toLowerCase();
  lista = lista.filter(sc =>
    ['receita', 'receitas'].includes(norm(sc.tipo ?? sc.tipo_plano ?? sc.categoria))
  );

  subcontas.value = lista;

  // Contas financeiras (sem mudanças)
  contasFinanceiras.value = [];
  contasMensagem.value = '';
  try {
    contasFinanceiras.value = await loadOperationalAccounts(route.params.id ? 'receita_geral_recebimento' : 'receita_geral_inclusao');
    if (!contasFinanceiras.value.length) contasMensagem.value = emptyAccountsMessage;
  } catch (error) { contasMensagem.value = accountSelectionError(error); }

  await carregarFormas();
}

async function carregarFormas() {
  try {
    const { data } = await axios.get('/cadastros/formas-pagamento');

    const ordemPersonalizada = {
      'pix': 1,
      'especie': 2,
      'cartao de credito a vista': 3,
      'cartao de credito parcelado': 4,
      'cartao de debito': 5,
      'boleto': 6,
      'transferencia bancaria': 7,
      'cheque': 8,
      'a informar': 9,
    };

    const lista = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);

    formasPagamento.value = lista
      .map(fp => ({
        ...fp,
        conta_financeira_padrao_id: fp.conta_financeira_padrao_id ?? null
      }))
      .sort((a, b) => {
        const oa = ordemPersonalizada[norm(a.nome)] ?? 999;
        const ob = ordemPersonalizada[norm(b.nome)] ?? 999;
        if (oa !== ob) return oa - ob;
        return a.nome.localeCompare(b.nome, 'pt-BR');
      });
  } catch (e) {
    console.error('Erro ao carregar formas de pagamento:', e);
    formasPagamento.value = [];
  }
}

// Se editando, carregar dados
async function carregarReceita(id) {
    // 1) Busca o cabeçalho e extrai o objeto real
    const res     = await axios.get(`/financeiro/receitas-gerais/${id}`);
    const payload = res.data.data;
    // atribui TODOS os campos do payload diretamente
    Object.assign(receita, payload);
    
    // console.log('📥 payload de receita:', payload);

    // normaliza datas para YYYY-MM-DD
    if (receita.primeiro_vencimento) {
        receita.primeiro_vencimento = receita.primeiro_vencimento.split('T')[0];
    }
    if (receita.criado_em) {
        receita.criado_em = receita.criado_em.split('T')[0];
    }

  // monta pagamentos
    const respPg = await axios.get(`/financeiro/receitas-gerais/${id}/pagamentos`);
    originalPayments = respPg.data.data || [];
    pagamentos.value = originalPayments.map(p => ({
      ...p,
      valor: Number(p.valor) || 0, // <<< garante número
      data_vencimento: p.data_vencimento?.split('T')[0] || '',
      data_pagamento:  p.data_pagamento ? p.data_pagamento.split('T')[0] : '',
      data_recebimento: p.data_recebimento?.split('T')[0] || '',
      numero_autorizacao: p.numero_autorizacao || null,
      conta_financeira_id: p.conta_financeira_id != null ? Number(p.conta_financeira_id) : null,
      forma_pagamento_id: p.forma_pagamento_id != null ? Number(p.forma_pagamento_id) : null,

    }));

    // Marcar recebimentos existentes como "manuais" para não serem recalculados
    pagamentos.value.forEach(p => {
      p._recebimento_manual = !!p.data_recebimento; // true se veio do banco
    });

    // Aguarda a troca do array e aplica o valor em todas
    await nextTick();
    aplicarValorEmTodasParcelas();

    // encerra a fase de hidratação (evita que os watchers recalcularem ao entrar na tela)
    hydrating.value = false;

  parcelasCount.value = payload.parcelas;
    // console.log('📝 receita após assign:', JSON.parse(JSON.stringify(receita)));
    // console.log('🔢 parcelasCount:', parcelasCount.value);

}

// Salvar create/update
async function salvar() {
  if (!await authorizeReceiptAction(savePermission.value)) return;
  if (!formValid.value) return;
  isSubmitting.value = true;

  const payload = {
    subconta: receita.subconta,
    descricao: receita.descricao,
    valor_total: receita.valor_total,
    parcelas: parcelasCount.value,
    periodicidade: receita.periodicidade,
    periodicidade_dias: receita.periodicidade === 'outro' ? receita.periodicidade_dias : null,
    primeiro_vencimento: receita.primeiro_vencimento,
    pagamentos: pagamentos.value.map(p => ({
      id: p.id,
      forma_pagamento_id:  p.forma_pagamento_id,
      data_vencimento:     p.data_vencimento,
      valor:               p.valor,
      data_pagamento:      p.data_pagamento || null,
      data_recebimento:    p.data_recebimento || null,
      conta_financeira_id: p.conta_financeira_id || null,
      banco_numero:        p.banco_numero || null,
      agencia:             p.agencia || null,
      cheque_numero:       p.cheque_numero || null,
      observacoes:         p.observacoes || null,
      numero_autorizacao:  isFormaComAutorizacao(p.forma_pagamento_id)
        ? ((p.numero_autorizacao || '').trim() || null)
        : null,
    })),
  };

  try {
    if (receita.id) {
      Object.assign(payload, paymentCollectionOperations(originalPayments,payload.pagamentos,'data_vencimento',{operationalAccountSelection:true}));
      await axios.put(
        `/financeiro/receitas-gerais/${receita.id}`,
        payload
      );
    } else {
      // Criação
      const res = await axios.post(
        '/financeiro/receitas-gerais',
        payload
      );
      // assume back-end retorna { data: { id, criado_em } }
      receita.id = res.data.data.id;
    }
    router.push('/financeiro/receitas-gerais');
  } catch (err) {
    console.error('Erro ao salvar receita geral:', err);
    alert('Não foi possível salvar. Veja o console para detalhes.');
  } finally {
    isSubmitting.value = false;
  }
}

async function removerParcela(idx) {
  if (pagamentos.value[idx]?.id && !await authorizeReceiptAction('excluir_receitas_gerais')) return;
  // remove do array
  pagamentos.value.splice(idx, 1);
  // recalcula número de parcelas
  parcelasCount.value = pagamentos.value.length;
}

// Função auxiliar para criar uma única parcela nova
function criarParcela(idx) {
  // mesma lógica de gerarPagamentos, mas só para 1 linha
  let dias = 30;
  switch (receita.periodicidade) {
    case 'quinzenal': dias = 15; break;
    case 'semanal':   dias = 7;  break;
    case 'outro':     dias = Number(receita.periodicidade_dias) || 1; break;
  }
  const dt0 = new Date(receita.primeiro_vencimento);
  dt0.setDate(dt0.getDate() + dias * idx);

  if (idx > 0) {
    // se cair em fim de semana/feriado, empurra para o próximo útil
    const isWeekend = [0, 6].includes(dt0.getDay());
    const ajustada = isWeekend ? globalUtils.nextBusinessDay(dt0) : dt0;
    // se quiser garantir também contra feriado (mesmo quando não é w/e), faça:
    // const ajustada = globalUtils.nextBusinessDay(dt0);
    dt0.setTime(ajustada.getTime());
  }

  const data_venc = dt0.toISOString().slice(0,10);
  return {
    id: null,
    receitas_gerais_id: receita.id,
    forma_pagamento_id: null,
    data_vencimento:    data_venc,
    valor:              Number(receita.valor_total) || 0,
    data_pagamento:     '',
    data_recebimento:   '',
    conta_financeira_id:null,
    banco_numero:       '',
    agencia:            '',
    cheque_numero:      '',
    observacoes:        '',
    numero_autorizacao: null,

  };
}


function cancelar() {
  router.push('/financeiro/receitas-gerais');
}



// ➍ Garanta que cada alteração no vencimento ou forma chame onParcelaChange:
watch(
  () => pagamentos.value.map(p => [p.data_vencimento, p.forma_pagamento_id]),
  () => {
    if (hydrating.value) return;               // ⬅️ adicionado
    pagamentos.value.forEach(p => onParcelaChange(p));
  },
  { deep: true }
);

// Substitua seu watch existente por este:
watch(
  () => parcelasCount.value,
  async (novo) => {
    // Normaliza leitura sem escrever de volta (evita loops)
    const n = Number.isFinite(novo) ? Math.max(1, Math.floor(novo)) : 1;
    const atual = pagamentos.value.length;

    if (n > atual) {
      // Adiciona novas linhas
      for (let i = atual; i < n; i++) {
        pagamentos.value.push(criarParcela(i));
      }
      await nextTick();
      aplicarValorEmTodasParcelas();
    } else if (n < atual) {
      // Remove do fim (sem mexer em parcelas já aprovadas)
      pagamentos.value.splice(n);
    }

    // Mantém o valor em header para validações/salvar
    receita.parcelas = n;
  },
  { flush: 'post' }
);


watch(
  () => pagamentos.value.map(p => [p.data_pagamento, p.forma_pagamento_id]),
  () => {
    if (hydrating.value) return;

    pagamentos.value.forEach(p => {
      if (!p) return;
      if (!p.data_pagamento || !p.forma_pagamento_id || isAInformar(p.forma_pagamento_id)) {
        p.data_recebimento = '';
        return;
      }

      if (!p._recebimento_manual) {
        if (isCartaoCreditoParcelado(p.forma_pagamento_id)) {
          recalcRecebimentoBlocoParcelado(p);
        } else {
          recalcRecebimento(p);
        }
      }
    });
  },
  { deep: true }
);


onMounted(async () => {
  await fetchPermissions();
  // (opcional) mantém sua busca de tipos
  const respTipos = await axios.get('/financeiro/subcontas/tipo_plano');
  tiposPlano.value = respTipos.data.data;
  if (!tiposPlano.value.includes(tipoSelecionado.value)) {
    tipoSelecionado.value = tiposPlano.value[0] || 'Receita';
  }

  // carrega listas especificamente para RECEITA
  await carregarListas('Receita');

  if (route.params.id) {
    await carregarReceita(route.params.id);
    valorStr.value = receita?.valor_total ? nfBR.format(Number(receita.valor_total)) : '';
  } else {
    receita.criado_em = new Date().toISOString();
    receita.primeiro_vencimento = dataRegistro.value;
    gerarPagamentos(parcelasCount.value);
    await nextTick();
    aplicarValorEmTodasParcelas();

    hydrating.value = false;

    valorStr.value = receita?.valor_total ? nfBR.format(Number(receita.valor_total)) : '';
  }
});

</script>

<style scoped>
.spacer {
  margin-bottom: 1rem;
}
.section-title {
  font-weight: bold;
  margin-bottom: 0.5rem;
}
.required {
  color: red;
}
.disabled-input {
  background: #e9ecef;
}
.form-actions {
  margin-top: 1.5rem;
}

/* Layout similar ao FormSection do formulário de Aula Avulsa */
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}

/* Títulos das seções */
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}

/* Estilização para campos desativados */
.disabled-input {
  background: #e9ecef;
  border: 1px solid #ced4da;
}

/* Ícones de ação */
.required {
  color: red;
}

</style>
